import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useConsumerAuth } from '../../context/ConsumerAuthContext';
import { Eye, EyeOff, AlertCircle, CheckCircle, Car, Home } from 'lucide-react';

const ConsumerSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup } = useConsumerAuth();
  
  const [step, setStep] = useState(1);
  const [loanType, setLoanType] = useState(searchParams.get('type') || 'auto');
  const [formData, setFormData] = useState({
    // Personal Info
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    // Address
    address: '',
    city: '',
    state: '',
    zip_code: '',
    // Identity
    ssn: '',
    date_of_birth: '',
    // Consent
    agree_terms: false,
    agree_credit_check: false
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
    setError('');
  };

  const formatSSN = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 9)}`;
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleSSNChange = (e) => {
    const formatted = formatSSN(e.target.value);
    setFormData({ ...formData, ssn: formatted });
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const validateStep1 = () => {
    if (!formData.first_name || !formData.last_name) {
      setError('Please enter your full name');
      return false;
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone || formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.address || !formData.city || !formData.state || !formData.zip_code) {
      setError('Please complete your address');
      return false;
    }
    if (!/^\d{5}(-\d{4})?$/.test(formData.zip_code)) {
      setError('Please enter a valid ZIP code');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (formData.ssn.replace(/\D/g, '').length !== 9) {
      setError('Please enter a valid 9-digit SSN');
      return false;
    }
    if (!formData.date_of_birth) {
      setError('Please enter your date of birth');
      return false;
    }
    if (!formData.agree_terms || !formData.agree_credit_check) {
      setError('Please agree to all terms to continue');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setLoading(true);
    setError('');

    try {
      await signup({
        ...formData,
        loan_type: loanType,
        phone: formData.phone.replace(/\D/g, ''),
        ssn_last_four: formData.ssn.slice(-4)
      });
      navigate('/beth-finance/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#012169] via-[#01337d] to-[#014091] flex flex-col">
      {/* Header */}
      <header className="bg-[#012169]/80 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/beth-finance" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#E31837] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white tracking-tight">BETH Finance</span>
                <span className="text-xs text-gray-300">Credit Enhancement Solutions</span>
              </div>
            </Link>
            <Link 
              to="/beth-finance/login" 
              className="text-white hover:text-[#E31837] text-sm font-medium transition-colors"
            >
              Already have an account? Sign In
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      step >= s ? 'bg-[#012169] text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                    </div>
                    {s < 3 && (
                      <div className={`w-24 md:w-32 h-1 mx-2 ${step > s ? 'bg-[#012169]' : 'bg-gray-200'}`}></div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2 text-sm">
                <span className={step >= 1 ? 'text-[#012169] font-medium' : 'text-gray-500'}>Account</span>
                <span className={step >= 2 ? 'text-[#012169] font-medium' : 'text-gray-500'}>Address</span>
                <span className={step >= 3 ? 'text-[#012169] font-medium' : 'text-gray-500'}>Verify</span>
              </div>
            </div>

            {/* Loan Type Selection */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">Select Loan Type</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setLoanType('auto')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    loanType === 'auto' 
                      ? 'border-[#012169] bg-[#012169]/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Car className={`w-8 h-8 mx-auto mb-2 ${loanType === 'auto' ? 'text-[#012169]' : 'text-gray-400'}`} />
                  <div className={`font-semibold ${loanType === 'auto' ? 'text-[#012169]' : 'text-gray-700'}`}>Auto Loan</div>
                  <div className="text-sm text-gray-500">$49 fee</div>
                </button>
                <button
                  type="button"
                  onClick={() => setLoanType('mortgage')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    loanType === 'mortgage' 
                      ? 'border-[#012169] bg-[#012169]/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Home className={`w-8 h-8 mx-auto mb-2 ${loanType === 'mortgage' ? 'text-[#012169]' : 'text-gray-400'}`} />
                  <div className={`font-semibold ${loanType === 'mortgage' ? 'text-[#012169]' : 'text-gray-700'}`}>Mortgage</div>
                  <div className="text-sm text-gray-500">$199 fee</div>
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
                <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Step 1: Personal Information */}
              {step === 1 && (
                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-[#012169] mb-4">Personal Information</h2>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                      placeholder="john.doe@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                      placeholder="(555) 555-5555"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                        placeholder="Minimum 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                    <input
                      type="password"
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                      placeholder="Confirm your password"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Address Information */}
              {step === 2 && (
                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-[#012169] mb-4">Address Information</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                      placeholder="123 Main Street, Apt 4B"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                        placeholder="New York"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                      >
                        <option value="">Select State</option>
                        {states.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                    <input
                      type="text"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      maxLength="10"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                      placeholder="10001"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: Identity Verification */}
              {step === 3 && (
                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-[#012169] mb-4">Identity Verification</h2>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Social Security Number</label>
                    <input
                      type="text"
                      name="ssn"
                      value={formData.ssn}
                      onChange={handleSSNChange}
                      maxLength="11"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                      placeholder="XXX-XX-XXXX"
                    />
                    <p className="text-xs text-gray-500 mt-1">Your SSN is encrypted and securely stored</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#012169] focus:border-transparent outline-none"
                    />
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-3 mt-6">
                    <label className="flex items-start">
                      <input 
                        type="checkbox" 
                        name="agree_terms"
                        checked={formData.agree_terms}
                        onChange={handleChange}
                        className="w-5 h-5 mt-0.5 text-[#012169] border-gray-300 rounded focus:ring-[#012169]" 
                      />
                      <span className="ml-3 text-sm text-gray-700">
                        I agree to the <Link to="/terms" className="text-[#E31837] hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-[#E31837] hover:underline">Privacy Policy</Link>
                      </span>
                    </label>

                    <label className="flex items-start">
                      <input 
                        type="checkbox" 
                        name="agree_credit_check"
                        checked={formData.agree_credit_check}
                        onChange={handleChange}
                        className="w-5 h-5 mt-0.5 text-[#012169] border-gray-300 rounded focus:ring-[#012169]" 
                      />
                      <span className="ml-3 text-sm text-gray-700">
                        I authorize BETH Finance to obtain my credit report for the purpose of evaluating my loan application
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-all"
                  >
                    Back
                  </button>
                ) : (
                  <div></div>
                )}

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-8 py-3 bg-[#012169] hover:bg-[#01337d] text-white rounded-lg font-semibold transition-all"
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-8 py-3 bg-[#E31837] hover:bg-[#c4142f] text-white rounded-lg font-semibold transition-all disabled:opacity-50"
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </button>
                )}
              </div>
            </form>

            {/* Security Notice */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-center text-sm text-gray-500">
                <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                <span>Your information is protected with 256-bit SSL encryption</span>
              </div>
            </div>
          </div>

          {/* Back to main site */}
          <div className="mt-6 text-center">
            <Link to="/beth-finance" className="text-white/80 hover:text-white text-sm transition-colors">
              ← Back to BETH Finance
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsumerSignup;
